import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, ScanBarcode, ArrowLeftRight } from 'lucide-react';
import { getSettings, updateSettings } from '../lib/api';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ 
    hide_scanner_buttons: false,
    enable_swipe_navigation: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        setSettings(res.data);
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      // Update localStorage cache
      localStorage.setItem('stockops_settings', JSON.stringify(settings));
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-enter" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="btn-icon"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-heading font-bold">App Settings</h1>
          <p className="text-sm text-muted-foreground">Configure app behavior</p>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="space-y-4">
        {/* Stock Out Features */}
        <div className="item-card">
          <h2 className="text-base font-semibold mb-4">Stock Out Features</h2>
          
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div className="flex items-start gap-3">
              <ScanBarcode className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Hide Scanner Buttons</p>
                <p className="text-sm text-muted-foreground">
                  Hide "Scan" and "Type Code" buttons on the Log Stock Out page
                </p>
              </div>
            </div>
            <Switch
              checked={settings.hide_scanner_buttons}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, hide_scanner_buttons: checked })
              }
              data-testid="hide-scanner-toggle"
            />
          </div>
        </div>

        {/* Navigation Features */}
        <div className="item-card">
          <h2 className="text-base font-semibold mb-4">Navigation</h2>
          
          <div className="flex items-center justify-between py-3">
            <div className="flex items-start gap-3">
              <ArrowLeftRight className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Swipe Navigation</p>
                <p className="text-sm text-muted-foreground">
                  Swipe left/right to navigate between pages
                </p>
              </div>
            </div>
            <Switch
              checked={settings.enable_swipe_navigation}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, enable_swipe_navigation: checked })
              }
              data-testid="swipe-navigation-toggle"
            />
          </div>
        </div>

        {/* Future Settings */}
        <div className="item-card bg-muted/30">
          <p className="text-sm text-muted-foreground text-center py-4">
            More settings coming soon...
          </p>
        </div>
      </div>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="btn-primary-action"
        data-testid="save-settings-btn"
      >
        {saving ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Saving...</span>
          </>
        ) : (
          <>
            <Save className="w-5 h-5" />
            <span>Save Settings</span>
          </>
        )}
      </Button>
    </div>
  );
};

export default SettingsPage;
